// ==================== AdminLogin.tsx ====================
import React, { useState, useEffect } from 'react';
import { verifyCredentials, initializeSession } from '../utils/userStorage';
import { getApiBaseUrl } from '../config/apiConfig';

interface AdminLoginData {
  email: string;
  password: string;
}

interface AdminLoginErrors {
  email?: string;
  password?: string;
  general?: string;
}

interface AdminLoginProps {
  onLoginSuccess: (adminData: any) => void;
  onGoToUserLogin: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess, onGoToUserLogin }) => {
  const [formData, setFormData] = useState<AdminLoginData>({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState<AdminLoginErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState<string>('');
  const [attemptsCount, setAttemptsCount] = useState(0);

  // ==================== INICIALIZACIÓN DE SESIÓN ====================
  useEffect(() => {
    console.log('🔍 Verificando sesión existente...');
    
    // Inicializar sesión al montar el componente
    const sessionStatus = initializeSession();
    
    if (sessionStatus.isAuthenticated && sessionStatus.user) {
      console.log('✅ Sesión activa encontrada:', sessionStatus.user.email);
      
      // Si es admin, redirigir automáticamente
      if (sessionStatus.user.role === 'admin') {
        console.log('👑 Admin autenticado, redirigiendo...');
        onLoginSuccess(sessionStatus.user);
      }
    }
  }, [onLoginSuccess]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: AdminLoginErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'El formato del email no es válido';
    }

    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name as keyof AdminLoginErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isBlocked) {
      setErrors({
        general: `Formulario bloqueado: ${blockReason}`
      });
      return;
    }
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      // 🔒 Verificar servidor disponible antes del login
      try {
        const healthCheck = await fetch(`${getApiBaseUrl()}/health`);
        if (!healthCheck.ok) throw new Error('Servidor no disponible');
        console.log('✅ Servidor disponible, procediendo con login de admin');
      } catch (serverError) {
        console.error('❌ Servidor no disponible:', serverError);
        setErrors({ general: 'El servidor no está disponible. Verifica tu conexión e inténtalo de nuevo.' });
        return;
      }
      
      let user = null;
      try {
        user = await verifyCredentials(formData.email, formData.password);
      } catch (loginError: any) {
        console.error('❌ Error al verificar credenciales:', loginError);
        setErrors({ general: loginError.message || 'Error de conexión con el servidor.' });
        return;
      }
      
      if (user && user.id && user.email && user.role === 'admin') {
        setAttemptsCount(0);
        setIsBlocked(false);
        setBlockReason('');
        
        console.log('✅ Login de admin exitoso para:', user.email);
        onLoginSuccess(user);
      } else {
        console.log('❌ Login de admin fallido - credenciales inválidas o no es admin');
        const newAttemptsCount = attemptsCount + 1;
        setAttemptsCount(newAttemptsCount);
        
        if (newAttemptsCount >= 3) {
          setIsBlocked(true);
          setBlockReason('Demasiados intentos fallidos. Espera 5 minutos antes de intentar nuevamente.');
          setErrors({ general: 'Demasiados intentos fallidos. El formulario ha sido bloqueado por 5 minutos por seguridad.' });

          setTimeout(() => {
            setIsBlocked(false);
            setBlockReason('');
            setAttemptsCount(0);
            setErrors({});
            console.log('🔓 Formulario de admin desbloqueado automáticamente');
          }, 5 * 60 * 1000);
          return;
        }
        
        setErrors({
          general: `Credenciales de administrador incorrectas. Te quedan ${3 - newAttemptsCount} intentos antes del bloqueo. Verifica tu email y contraseña.`
        });
        return;
      }

    } catch (error) {
      console.error('Error en el login de admin:', error);
      const newAttemptsCount = attemptsCount + 1;
      setAttemptsCount(newAttemptsCount);

      if (newAttemptsCount >= 3) {
        setIsBlocked(true);
        setBlockReason('Demasiados intentos fallidos. Espera 5 minutos antes de intentar nuevamente.');
        setErrors({ general: 'Demasiados intentos fallidos. El formulario ha sido bloqueado por 5 minutos por seguridad.' });

        setTimeout(() => {
          setIsBlocked(false);
          setBlockReason('');
          setAttemptsCount(0);
          setErrors({});
          console.log('🔓 Formulario de admin desbloqueado automáticamente');
        }, 5 * 60 * 1000);
        return;
      }

      setErrors({
        general: `Error de conexión. Te quedan ${3 - newAttemptsCount} intentos antes del bloqueo. Inténtalo de nuevo.`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold mb-6 text-center">Admin Login</h2>

        {errors.general && (
          <div className="bg-red-100 text-red-700 p-2 mb-4 rounded">{errors.general}</div>
        )}

        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            name="email"
            id="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`mt-1 block w-full p-2 border rounded ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
            disabled={isSubmitting || isBlocked}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        <div className="mb-6">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">Contraseña</label>
          <input
            type="password"
            name="password"
            id="password"
            value={formData.password}
            onChange={handleInputChange}
            className={`mt-1 block w-full p-2 border rounded ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
            disabled={isSubmitting || isBlocked}
          />
          {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting || isBlocked}
          className="w-full bg-orange-500 text-white p-2 rounded hover:bg-orange-600 disabled:opacity-50"
        >
          {isSubmitting ? 'Ingresando...' : 'Ingresar'}
        </button>

        <button
          type="button"
          onClick={onGoToUserLogin}
          className="w-full mt-4 text-sm text-blue-500 hover:underline"
        >
          Volver al login de usuario
        </button>
      </form>
    </div>
  );
};

export default AdminLogin;
